import { useEffect, useState } from 'react'
import { supabase } from '../../../../lib/supabase'

const CLUB_ID = 'a0000000-0000-0000-0000-000000000001'

export default function ClubProfile() {
  const [club, setClub] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState(null)
  const [formData, setFormData] = useState({ name: '' })
  const [logoUrl, setLogoUrl] = useState(null)

  useEffect(() => {
    const fetchClub = async () => {
      const { data } = await supabase.from('clubs').select('*').eq('id', CLUB_ID).single()
      if (data) { setClub(data); setFormData({ name: data.name }); setLogoUrl(data.logo_url) }
      setLoading(false)
    }
    fetchClub()
  }, [])

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    if (!file.type.startsWith('image/')) { setError('Please upload an image file'); return }
    if (file.size > 2 * 1024 * 1024) { setError('Image must be under 2MB'); return }
    setUploading(true)
    setError(null)
    const fileExt = file.name.split('.').pop()
    const fileName = `${CLUB_ID}-logo.${fileExt}`
    const { error: uploadError } = await supabase.storage.from('club-logos').upload(fileName, file, { upsert: true })
    if (uploadError) { setError(uploadError.message); setUploading(false); return }
    const { data: { publicUrl } } = supabase.storage.from('club-logos').getPublicUrl(fileName)
    const { error: updateError } = await supabase.from('clubs').update({ logo_url: publicUrl }).eq('id', CLUB_ID)
    if (updateError) { setError(updateError.message); setUploading(false); return }
    setLogoUrl(publicUrl)
    setUploading(false)
    setSuccess(true)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setSuccess(false)
    setError(null)
    const { error } = await supabase.from('clubs').update({ name: formData.name }).eq('id', CLUB_ID)
    if (error) { setError(error.message) } else { setSuccess(true) }
    setSaving(false)
  }

  const inputClass = "bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 focus:border-green-600 text-gray-900 dark:text-white px-4 py-3 text-sm outline-none transition-colors w-full"
  const labelClass = "text-xs text-gray-500 uppercase tracking-widest font-semibold"

  if (loading) return null

  return (
    <div className="bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 p-5 sm:p-8">
      <div className="text-xs text-gray-500 uppercase tracking-widest font-semibold mb-6">Club Profile</div>

      {success && <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 text-green-600 dark:text-green-400 px-4 py-3 text-sm mb-4">Club profile updated successfully</div>}
      {error && <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-red-600 dark:text-red-400 px-4 py-3 text-sm mb-4">{error}</div>}

      {/* Logo Upload */}
      <div className="flex flex-col gap-3 mb-8 max-w-lg">
        <label className={labelClass}>Club Logo</label>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
          <div className="w-20 h-20 border border-gray-200 dark:border-white/10 bg-gray-100 dark:bg-white/5 flex items-center justify-center overflow-hidden shrink-0">
            {logoUrl ? (
              <img src={logoUrl} alt="Club logo" className="w-full h-full object-contain" />
            ) : (
              <span className="text-gray-400 text-xs uppercase tracking-widest">No Logo</span>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <label className="bg-gray-200 dark:bg-white/10 hover:bg-gray-300 dark:hover:bg-white/20 text-gray-700 dark:text-white px-4 py-2 text-xs font-bold uppercase tracking-widest cursor-pointer transition-colors w-fit">
              {uploading ? 'Uploading...' : 'Choose Image'}
              <input type="file" accept="image/*" onChange={handleLogoUpload} disabled={uploading} className="hidden" />
            </label>
            <p className="text-gray-400 text-xs">PNG, JPG or SVG — max 2MB</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4 max-w-lg">
        <div className="flex flex-col gap-2">
          <label className={labelClass}>Club Name</label>
          <input type="text" value={formData.name} onChange={(e) => setFormData({ name: e.target.value })} className={inputClass} />
        </div>
        <div className="flex flex-col gap-2">
          <label className={labelClass}>Subscription Plan</label>
          <div className="px-4 py-3 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/5 text-gray-500 text-sm capitalize">{club?.subscription_plan}</div>
        </div>
        <div className="flex flex-col gap-2">
          <label className={labelClass}>Club ID</label>
          <div className="px-4 py-3 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/5 text-gray-500 text-xs font-mono break-all">{club?.id}</div>
        </div>
        <div className="flex flex-col gap-2">
          <label className={labelClass}>Member Since</label>
          <div className="px-4 py-3 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/5 text-gray-500 text-sm">
            {new Date(club?.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </div>
        </div>
        <button type="submit" disabled={saving} className="bg-green-600 hover:bg-green-700 disabled:bg-green-900 text-white py-3 font-bold uppercase tracking-widest text-sm transition-colors mt-2 w-fit px-8">
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </form>
    </div>
  )
}